var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { PureComponent, isValidElement, cloneElement, createRef, } from 'react';
import { findDOMNode } from 'react-dom';
import { patchResizeHandler, isFunction, isSSR, isDOMElement, createNotifier } from './utils';
class ResizeDetector extends PureComponent {
    constructor(props) {
        super(props);
        this.cancelHandler = () => {
            if (this.resizeHandler && this.resizeHandler.cancel) {
                this.resizeHandler.cancel();
                this.resizeHandler = null;
            }
        };
        this.attachObserver = () => {
            const { targetRef, observerOptions } = this.props;
            if (isSSR()) {
                return;
            }
            if (targetRef && targetRef.current) {
                this.targetRef.current = targetRef.current;
            }
            const element = this.getElement();
            if (!element) {
                return;
            }
            if (this.observableElement && this.observableElement === element) {
                return;
            }
            this.observableElement = element;
            this.resizeObserver.observe(element, observerOptions);
        };
        this.getElement = () => {
            const { querySelector, targetDomEl } = this.props;
            if (isSSR())
                return null;
            // 通过查询选择器获取
            if (querySelector)
                return document.querySelector(querySelector);
            if (targetDomEl && isDOMElement(targetDomEl))
                return targetDomEl;
            if (this.targetRef && isDOMElement(this.targetRef.current))
                return this.targetRef.current;
            const currentElement = findDOMNode(this);
            if (!currentElement)
                return null;
            const renderType = this.getRenderType();
            switch (renderType) {
                case 'renderProp':
                    return currentElement;
                case 'childFunction':
                    return currentElement;
                case 'child':
                    return currentElement;
                case 'childArray':
                    return currentElement;
                default:
                    return currentElement.parentElement;
            }
        };
        this.createResizeHandler = (entries) => {
            const { handleWidth = true, handleHeight = true, onResize } = this.props;
            if (!handleWidth && !handleHeight)
                return;
            const notifyResize = createNotifier(onResize, this.setState.bind(this), handleWidth, handleHeight);
            entries.forEach((entry) => {
                const { width, height } = (entry && entry.contentRect) || {};
                const shouldSetSize = !this.skipOnMount && !isSSR();
                if (shouldSetSize) {
                    notifyResize({ width, height });
                }
                this.skipOnMount = false;
            });
        };
        this.getRenderType = () => {
            const { render, children } = this.props;
            if (isFunction(render)) {
                return 'renderProp';
            }
            if (isFunction(children)) {
                return 'childFunction';
            }
            if (isValidElement(children)) {
                return 'child';
            }
            if (Array.isArray(children)) {
                return 'childArray';
            }
            return 'parent';
        };
        const { skipOnMount, refreshMode, refreshRate = 1000, refreshOptions } = props;
        this.state = {
            width: undefined,
            height: undefined,
        };
        this.skipOnMount = skipOnMount;
        this.targetRef = createRef();
        this.observableElement = null;
        if (isSSR()) {
            return;
        }
        this.resizeHandler = patchResizeHandler(this.createResizeHandler, refreshMode, refreshRate, refreshOptions);
        this.resizeObserver = new window.ResizeObserver(this.resizeHandler);
    }
    componentDidMount() {
        this.attachObserver();
    }
    componentDidUpdate() {
        this.attachObserver();
    }
    componentWillUnmount() {
        if (isSSR()) {
            return;
        }
        this.resizeObserver.disconnect();
        this.cancelHandler();
    }
    render() {
        const { render, children, nodeType: WrapperTag = 'div' } = this.props;
        const { width, height } = this.state;
        const childProps = { width, height, targetRef: this.targetRef };
        const renderType = this.getRenderType();
        let typedChildren;
        switch (renderType) {
            case 'renderProp':
                return render && render(childProps);
            case 'childFunction':
                typedChildren = children;
                return typedChildren(childProps);
            case 'child':
                typedChildren = children;
                if (typedChildren.type && typeof typedChildren.type === 'string') {
                    const { targetRef } = childProps, nativeProps = __rest(childProps, ["targetRef"]);
                    return cloneElement(typedChildren, nativeProps);
                }
                return cloneElement(typedChildren, childProps);
            case 'childArray':
                typedChildren = children;
                return typedChildren === null || typedChildren === void 0 ? void 0 : typedChildren.map((el) => !!el && cloneElement(el, childProps));
            default:
                return <WrapperTag />;
        }
    }
}
export default ResizeDetector;
